import '../styles/global.css';
import '@trussworks/react-uswds/lib/uswds.css';
import '@trussworks/react-uswds/lib/index.css';

import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Chip from '@mui/material/Chip';
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter';
import { Account } from '@toolpad/core/Account';
import type { Navigation } from '@toolpad/core/AppProvider';
import { type Session } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { NextAppProvider } from '@toolpad/core/nextjs';
import { PageContainer } from '@toolpad/core/PageContainer';
import {
  GovBanner,
  Identifier,
  IdentifierGov,
  IdentifierIdentity,
  IdentifierLink,
  IdentifierLinkItem,
  IdentifierLinks,
  IdentifierLogo,
  IdentifierLogos,
  IdentifierMasthead,
  Link,
} from '@trussworks/react-uswds';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Image from 'next/image';
import gsaLogo from 'public/gsa-logo-c-m.png';
import dotGovIcon from 'public/gsa-logo-m.png';
import * as React from 'react';

import theme from '../theme';

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const demoSession = {
  user: {
    name: 'Eric Kersey',
    email: 'ekersey@nchannel.com',
    image: 'https://avatars.githubusercontent.com/u/16627754',
  },
};

const NAVIGATION: Navigation = [
  {
    kind: 'header',
    title: 'Main items',
  },
  {
    segment: '',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    segment: 'orders',
    title: 'Orders',
    icon: <ShoppingCartIcon />,
    children: [
      {
        kind: 'page',
        segment: 'pending-approval',
        title: 'Pending Approval',
        action: <Chip label={4} color="error" size="small" />,
      },
      {
        kind: 'page',
        segment: 'approved',
        title: 'Approved',
        action: <Chip label={12} color="success" size="small" />,
      },
      {
        kind: 'page',
        segment: 'declined',
        title: 'Declined',
      },
    ],
  },
];

const BRANDING = {
  title: 'Store Portal Approvers',
  logo: <Image src={gsaLogo} alt="Logo" width={48} height={40} />,
  homeUrl: '/',
};

const testIdentifierLogo = (
  <Image
    src={dotGovIcon}
    className="usa-identifier__logo-img"
    alt="Test Agency Name logo"
    width={40}
    height={40}
  />
);

const testLinks = (
  <>
    <IdentifierLinkItem key="one">
      <IdentifierLink href="#">About &lt;Parent shortname&gt;</IdentifierLink>
    </IdentifierLinkItem>
    <IdentifierLinkItem key="two">
      <IdentifierLink href="#">Accessibility support</IdentifierLink>
    </IdentifierLinkItem>
    <IdentifierLinkItem key="three">
      <IdentifierLink href="#">FOIA requests</IdentifierLink>
    </IdentifierLinkItem>
    <IdentifierLinkItem key="four">
      <IdentifierLink href="#">No FEAR Act data</IdentifierLink>
    </IdentifierLinkItem>
    <IdentifierLinkItem key="five">
      <IdentifierLink href="#">Office of the Inspector General</IdentifierLink>
    </IdentifierLinkItem>
    <IdentifierLinkItem key="six">
      <IdentifierLink href="#">Performance reports</IdentifierLink>
    </IdentifierLinkItem>
    <IdentifierLinkItem key="seven">
      <IdentifierLink href="#">Privacy policy</IdentifierLink>
    </IdentifierLinkItem>
  </>
);

const testIdentifierGovContent = (
  <>
    <div className="usa-identifier__usagov-description">
      Looking for U.S. government information and services?
    </div>
    &nbsp;
    <Link href="https://www.usa.gov/" className="usa-link">
      Visit USA.gov
    </Link>
  </>
);

function getDefaultLayout(page: React.ReactElement) {
  return (
    <>
      <GovBanner />
      <DashboardLayout>
        <PageContainer>{page}</PageContainer>
      </DashboardLayout>
      <Identifier>
        <IdentifierMasthead aria-label="Agency identifier">
          <IdentifierLogos>
            <IdentifierLogo href="#">{testIdentifierLogo}</IdentifierLogo>
          </IdentifierLogos>
          <IdentifierIdentity domain="domain.edu.mil.gov">
            <span aria-hidden="true">An</span>
            {` official website of the `}
            <Link href="#">Test Agency Name</Link>
          </IdentifierIdentity>
        </IdentifierMasthead>
        <IdentifierLinks
          navProps={{
            'aria-label': 'Important links',
          }}
        >
          {testLinks}
        </IdentifierLinks>
        <IdentifierGov aria-label="U.S. government information and services">
          {testIdentifierGovContent}
        </IdentifierGov>
      </Identifier>
    </>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(demoSession);
  const authentication = React.useMemo(() => {
    return {
      signIn: () => {
        setSession(demoSession);
      },
      signOut: () => {
        setSession(null);
      },
    };
  }, []);
  return (
    <React.Fragment>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>
      <NextAppProvider
        navigation={NAVIGATION}
        branding={BRANDING}
        theme={theme}
        session={session}
        authentication={authentication}
      >
        <Account />
        {children}
      </NextAppProvider>
    </React.Fragment>
  );
}

export default function App(props: AppPropsWithLayout) {
  const { Component, pageProps } = props;

  const getLayout = Component.getLayout ?? getDefaultLayout;
  let pageContent = getLayout(<Component {...pageProps} />);
  pageContent = <AppLayout>{pageContent}</AppLayout>;

  return <AppCacheProvider {...props}>{pageContent}</AppCacheProvider>;
}
